import {
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  HostListener,
  input, linkedSignal,
  model,
  OnInit,
  output,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';

// Overlay & Portal -importit
import {Overlay, OverlayConfig, OverlayRef,} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {RalToHexPipe} from "../../../pipe/ral-to-hex.pipe";
import {FormControl} from "@angular/forms";
import {TranslatePipe} from "@ngx-translate/core";
import {isDefined} from "../../../../core/util/isDefined";

export interface SelectOption {
  value: string | number;
  label?: string;
}

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [NgTemplateOutlet, RalToHexPipe, TranslatePipe],
  templateUrl: './select.component.html',
})
export class SelectComponent implements OnInit{
  /** Placeholder dynaamisena input-signaalina */
  placeholder = input<string>('Valitse');

  /** Optiot signaalina */
  options = input<SelectOption[]>([]);

  /** Disabled-tila signaalina */
  disabled = input<boolean>(false);

  /** Initial selected value */
  initialValue = input<string | number>(null);

  /** Linked value */
  linkedValue = model<any>(null);

  /** Custom content -template */
  customContent?: Signal<any> = contentChild('customContent');

  /** Valittu arvo signaalina */
  selectedValue = linkedSignal<any>(this.linkedValue);

  /** Dropdownin auki/kiinni tila */
  isOpen = signal<boolean>(false);

  /** Fokusoitu optionin indeksi signaalina */
  focusedIndex = signal<number | null>(null);

  /** Tarkistus: Onko mitään valittuna? */
  hasSelection = computed(() => isDefined(this.selectedValue()));

  /** Valittu optio */
  selectedOption = computed(() => this.options().find(o => o.value === this.selectedValue()));

  /** Angular formControl bind */
  formController = input<FormControl>(new FormControl());

  selectedOptionOutput = output<SelectOption | null | unknown>();

  showColorIndicator = input<boolean>(false);

  labelPrefix = input<string|null>('');

  translate = input<boolean>(false);

  // OverlayRef-viite, jotta voimme sulkea sen myöhemmin
  private overlayRef?: OverlayRef;

  private selected: unknown;
  private initDone: boolean = false;

  // Otetaan dropdownin <ng-template #dropdownTemplate> talteen
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;

  constructor(private elRef: ElementRef<HTMLElement>,
              private viewContainerRef: ViewContainerRef,
              private overlay: Overlay
  ) {

    // Päivitetään formControlin arvo, kun valittu arvo muuttuu
    effect(() => {
      this.formController()?.setValue(this.selectedValue());
    });

    // Päivitetään fokusoitu indeksi, kun lista avataan
    effect(() => {
      if (this.isOpen()) {
        this.updateFocusedIndex();
        this.openOverlay();   // Avataan overlay, jos asetus onOpen -> true
      } else {
        this.closeOverlay();  // Suljetaan overlay, jos asetus onOpen -> false
      }

      if( this.selectedOption() !== this.selected ){
        this.selected = this.selectedOption();
        this.selectedOptionOutput.emit(this.selected ? { ...this.selectedOption() } : null);
      }

    });


  }

  ngOnInit() {
    if (this.initialValue() !== null) {
      this.selectedValue.set(this.initialValue());
    }

    this.selected = this.selectedOption();
    this.initDone=true; // Prevent output emit too early. TODO: Find better solution
  }

  updateFocusedIndex() {
    const options = this.options();
    if (options && options.length > 0) {
      const selIndex = options.findIndex(o => o.value === this.selectedValue());
      this.focusedIndex.set(selIndex >= 0 ? selIndex : 0);
    } else {
      this.focusedIndex.set(null);
    }
  }

  /** Toggle dropdown open/close */
  toggleOpen() {
    if (!this.disabled()) {
      this.isOpen.update(open => !open);
    }
  }

  close() {
    this.isOpen.set(false);
  }

  /** Valitse optio indeksin mukaan */
  selectOptionByIndex(index: number) {
    if (this.disabled() || index < 0 || index >= this.options().length) return;
    this.selectedValue.set(this.options()[index].value);
    this.closeDropdown();
  }

  /** Valitse optio suoraan */
  selectOption(option: SelectOption) {
    if (this.disabled()) return;
    this.selectedValue.set(option.value);
    this.closeDropdown();
  }

  /** Sulje dropdown */
  closeDropdown() {
    this.isOpen.set(false);
    this.focusedIndex.set(null);
  }

  private openOverlay() {
    // Jos overlayRef on jo olemassa eikä ole detachattuna, ei luoda uutta
    if (this.overlayRef?.hasAttached()) {
      return;
    }

    const originRect = this.elRef.nativeElement.getBoundingClientRect();
    const selectWidth = originRect.width;

    // Määritellään overlayn position-säädöt
    const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.elRef)  // ankkuroi overlayn "this.elRef" (selectin root) elementtiin
        .withPositions([
          // Näytä dropdown valintaelementin alapuolella
          {
            originX: 'start',
            originY: 'bottom',  // ankkuri alapäästä
            overlayX: 'start',
            overlayY: 'top',    // overlay piirtyy yläreunastaan kiinni ankkurissa
          },
          // Toissijainen sijainti, jos ei mahdu alas, näytä yläpuolella
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ]);

    // Luodaan overlay, jos sitä ei vielä ole
    if (!this.overlayRef) {
      const overlayConfig: OverlayConfig = {
        positionStrategy,
        hasBackdrop: true,    // klikattaessa taustaa -> sulkeutuu
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        width: selectWidth,
      };
      this.overlayRef = this.overlay.create(overlayConfig);

      // Suljetaan dropdown, jos käyttäjä klikkaa overlayn ulkopuolelle
      this.overlayRef.backdropClick().subscribe(() => {
        this.closeDropdown();
      });
    } else {
      // Jos overlayRef on luotu, päivitetään sen positionStrategy
      this.overlayRef.updatePositionStrategy(positionStrategy);
    }

    // Liitetään dropdownin sisältö (template) overlayhin TemplatePortalin kautta
    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  private closeOverlay(dispose = false) {
    if (this.overlayRef) {
      this.overlayRef.detach();
      if (dispose) {
        this.overlayRef.dispose();
        this.overlayRef = undefined;
      }
    }
  }

  /** Siirrä fokus seuraavaan optioon */
  focusNextOption() {
    const current = this.focusedIndex();
    if (this.options().length === 0) return;
    const nextIndex = current === null ? 0 : Math.min(current + 1, this.options().length - 1);
    this.focusedIndex.set(nextIndex);
  }

  /** Siirrä fokus edelliseen optioon */
  focusPreviousOption() {
    const current = this.focusedIndex();
    if (this.options().length === 0) return;
    const prevIndex = current === null ? 0 : Math.max(current - 1, 0);
    this.focusedIndex.set(prevIndex);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.disabled()) return;

    if (event.key === 'Enter') {
      if (this.isOpen() && this.focusedIndex() !== null) {
        event.preventDefault();
        this.selectOptionByIndex(this.focusedIndex()!);
      } else {
        event.preventDefault();
        this.isOpen.set(true);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen()) {
        this.isOpen.set(true);
      } else {
        this.focusNextOption();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen()) {
        this.isOpen.set(true);
      } else {
        this.focusPreviousOption();
      }
    } else if (event.key === 'Escape') {
      if (this.isOpen()) {
        event.preventDefault();
        this.closeDropdown();
      }
    }
  }

  //FIXME: Oletettavasti modalin overlay selkoittaa cdk overlayn ja tämä toistaiseksi kierretään
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Jos dropdown ei ole auki, ei tarvitse tehdä mitään
    if (!this.isOpen()) return;

    // 1) Kysytään: onko klikkaus valintakomponentin sisällä:
    const clickedInsideHost = this.elRef.nativeElement.contains(event.target as Node);

    // 2) Tai onko klikkaus overlayn sisällä:
    const clickedInsideOverlay = this.overlayRef
        ? this.overlayRef.overlayElement.contains(event.target as Node)
        : false;

    // Jos ei hostin EIKÄ overlayn sisällä -> suljetaan dropdown
    if (!clickedInsideHost && !clickedInsideOverlay) {
      this.closeDropdown();
    }
  }

  clear(){
    this.selectedValue.set(null);
  }

  parseString(label: string | number) {
    return typeof label === 'string' ? label : label.toString();
  }
}
